/** Copyright © 2024 Qcells. All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized use,
 * duplication, or distribution of software is strictly prohibited.
 */
import type { ValueType } from 'hems/common/utils';

export const GEN_TYPE = {
  GEN2: 'GEN2',
  GEN3: 'GEN3',
  FOX_ESS: 'FOX_ESS',
  FOX_ESS_H3: 'FOX_ESS_H3',
  AC_SYS: 'AC_SYS',
  DC_OPTIMIZER_GEN2: 'DC_OPTIMIZER_GEN2',
} as const;

export type GenTypeValue = ValueType<typeof GEN_TYPE>;

export const AC_DEVICE_TYPE = {
  /** Stand Alone AC Combiner  (case 1) */
  AC_COMBINER: 'AC_COMB',
  /** AC Combiner + Multi ESS + Hub (case 3) */
  AC_COMBINER_MULTI_ESS_HUB: 'AC_COMB_MULTI_ESS_HUB',
  /** Third Party + Multi ESS + Hub (case 4-1) */
  AC_THIRD_PARTY_MULTI_ESS_HUB: 'AC_THIRD_PARTY_MULTI_ESS_HUB',
  /** Third Party + Stand Alone ESS + Hub (case 4-2) */
  AC_THIRD_PARTY_ESS_HUB: 'AC_THIRD_PARTY_ESS_HUB',
  /** Third Party + Stand Alone ESS (case 5) */
  AC_THIRD_PARTY_ESS: 'AC_THIRD_PARTY_ESS',
  /** AC Combiner + Multi ESS (case 6) */
  AC_COMBINER_MULTI_ESS: 'AC_COMB_MULTI_ESS',
  /** Stand Alone AC ESS (case 8-1) */
  AC_ESS: 'AC_ESS',
  /** AC Multi ESS (case 8-2) */
  AC_MULTI_ESS: 'AC_MULTI_ESS',
} as const;

export type ACDeviceTypeValue = ValueType<typeof AC_DEVICE_TYPE>;

export const AC_MODEL_TYPE = {
  AC_COMBINER: 'ACCB',
  AC_ESS: 'ACES',
} as const;

export type ACModelTypeValue = ValueType<typeof AC_MODEL_TYPE>;

export const EMS_TYPE = {
  STAND_ALONE: '0',
  PRIMARY: '1',
  SECONDARY: '2',
} as const;

export type EmsTypeValue = ValueType<typeof EMS_TYPE>;

export const EMS_PRIORITY_ORDER_MAP: { [key in EmsTypeValue]: 1 | 2 | 3 } = {
  [EMS_TYPE.STAND_ALONE]: 1,
  [EMS_TYPE.PRIMARY]: 2,
  [EMS_TYPE.SECONDARY]: 3,
};

export const ACSystemModelNamePrefixes = ['AACCB', 'AACES'] as const;

export const CONSUMPTION_CT_TYPE = {
  GRID_SIDE: 0,
  LOAD_SIDE: 1,
  NOT_INSTALLED: 2,
} as const;

export type ConsumptionCTValue = ValueType<typeof CONSUMPTION_CT_TYPE>;

export const EXTERNAL_PRODUCTION_CT_TYPE = {
  NOT_INSTALLED: 0,
  INSTALLED: 1,
} as const;

export const HUB_EXTERNAL_CT_INSTALLED_POINT = {
  NON_INSTALLED: 0,
  GRID: 1,
  NON_BACKUP_LOAD: 2,
} as const;

export type HubExternalCtInstalledPointValue = ValueType<typeof HUB_EXTERNAL_CT_INSTALLED_POINT>;

export const HUB_GENERATOR_FLAG = {
  NOT_USED: 0,
  USED: 1,
} as const;

export type HubGeneratorFlagTypeValue = ValueType<typeof HUB_GENERATOR_FLAG>;

export const HUB_GENERATOR_REMOTE_ON = {
  IMPOSSIBLE: 0,
  POSSIBLE: 1,
} as const;

export type HubGeneratorRemoteOnTypeValue = ValueType<typeof HUB_GENERATOR_REMOTE_ON>;

export const THIRD_PARTY_CONTROL_TYPE = {
  ENABLE: '1',
  DISABLE: '0',
} as const;

export type ThirdPartyControlTypeValue = ValueType<typeof THIRD_PARTY_CONTROL_TYPE>;

export const HISTORY_INFO_CHANGE_TYPE_CODE = {
  INSERT: 'INSERT',
  UPDATE: 'UPDATE',
  REPLACE: 'REPLACE',
  REPLACE_MI: 'REPLACE_MI',
  DELETE: 'DELETE',
} as const;

export type HistoryInfoChangeTypeCodeValue = ValueType<typeof HISTORY_INFO_CHANGE_TYPE_CODE>;

export const HISTORY_DEVICE_TYPE_CD = {
  AC_COMB: 'AC_COMB',
  AC_ESS: 'AC_ESS',
  FOX_GW_EMS: 'FOX_GW_EMS',
  FOX_INV: 'FOX_INV',
  GW_INV: 'GW_INV',
} as const;

export type HistoryDeviceTypeCdValue = ValueType<typeof HISTORY_DEVICE_TYPE_CD>;

export const OPERATION_MODE = {
  /** No Operation */
  NOP: 0,
  /** Auto */
  AUTO: 1,
  /** Manual */
  MANUAL: 2,
} as const;
export type OperationModeValue = ValueType<typeof OPERATION_MODE>;

export const OPERATION_MODE_I18N_KEY_MAP: Record<OperationModeValue, string> = {
  [OPERATION_MODE.NOP]: 'control.nop',
  [OPERATION_MODE.AUTO]: 'device.auto',
  [OPERATION_MODE.MANUAL]: 'common.manual',
};

export const DEVICE_CONNECTION_RESPONSE_CODE = {
  ONLINE: '0',
  OFFLINE: '128',
} as const;

// 현재는 Generator 모델이 하나밖에 없어 아래의 모델명이 기본으로 보여지도록 처리. 추후 변경될 수 있음
export const GENERATOR_MODEL_NAME = 'Guardian 14kW #7223';

export const INSTALLER_DEFAULT_GENERATOR_NAMEPLATE_POWER = 14000;

/** Cloud passkey validation status (Cognito) */
export const CLOUD_PASSKEY_STATUS = {
  /** Password successfully authenticated by Cognito. */
  VALID: 'VALID',
  /** Password found in the database but failed Cognito authentication. */
  INVALID: 'INVALID',
  /** Password not found in the database or missing from Cognito. */
  NOT_EXISTS: 'NOT_EXISTS',
  /** An unexpected error occurred during the password validation process. */
  UNKNOWN: 'UNKNOWN',
} as const;

export type CloudPasskeyStatusValue = ValueType<typeof CLOUD_PASSKEY_STATUS>;
